import type { InitConfiguration } from '@datadog/browser-core';
import type { RumInitConfiguration } from '@datadog/browser-rum';

import type { ConfigState } from '@portal/store/src/configuration/types';

export enum DDEnv {
  Dev = 'dev',
  Prod = 'prod',
  Stage = 'stage',
}

export const getDDEnv = (config: ConfigState): string | undefined => {
  if (config.IS_PRODUCTION_ENV) {
    return DDEnv.Prod;
  }

  // Check for dev environment (new .engine.tech domain and legacy .evenfinancial domain)
  if (
    config.PORTAL_API_URL?.includes('dev-api.engine.tech') ||
    config.PORTAL_API_URL?.includes('dev-api.evenfinancial')
  ) {
    return DDEnv.Dev;
  }

  // Check for stage environment (new .engine.tech domain and legacy .evenfinancial domain)
  if (
    config.PORTAL_API_URL?.includes('stage-api.engine.tech') ||
    config.PORTAL_API_URL?.includes('api.evenfinancial')
  ) {
    return DDEnv.Stage;
  }

  // Fallback for local development
  if (config.PORTAL_API_URL?.includes('localhost')) {
    return DDEnv.Dev;
  }

  return undefined;
};

export const getDDBaseConfig = (
  config: ConfigState
): Omit<InitConfiguration, 'beforeSend'> => {
  const env = getDDEnv(config);
  const clientToken = config.DATADOG_CLIENT_TOKEN;

  const allowedTracingOrigins = [
    'api',
    'dev-api',
    'stage-api',
    'controls',
    'controls-dev',
    'controls-stage',
  ].map((subdomain) => `https://${subdomain}.evenfinancial.com`);

  return {
    allowedTracingOrigins,
    clientToken,
    env,
    sampleRate: 100,
    service: 'portal-app',
    site: 'datadoghq.com',
    version: config.DD_VERSION,
  };
};

export const getDDRumBaseConfig = (
  config: ConfigState
): Partial<RumInitConfiguration> => {
  const baseConfig = getDDBaseConfig(config);

  return {
    clientToken: baseConfig.clientToken,
    defaultPrivacyLevel: 'mask-user-input',
    env: baseConfig.env,
    service: baseConfig.service,
    sessionSampleRate: 100,
    site: 'datadoghq.com',
    version: baseConfig.version,
  };
};

// Re-export RUM functions for convenience
export { init as initRum, setUser as setRumUser } from './datadog-rum';
